'use client'

import { Button } from '@vinted/web-ui'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useIsClient } from 'usehooks-ts'

import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import { urlWithParams } from '@marketplace-web/browser/url-util'
import { ShippingDetailsContext } from '@marketplace-web/item-page/item-page-data'

import { getHomepageSessionIdFromUrl } from '@marketplace-web/home/home-page-util'
import { getSearchSessionData } from '@marketplace-web/search/search-feature'
import { ConversationInitiatorType } from '@marketplace-web/messaging/conversation-messaging-data'
import { buyerWantItemEvent, clickEvent } from '@marketplace-web/messaging/tracking-data'
import { useConversationNavigator } from '@marketplace-web/messaging/navigation-feature'

import { useRefUrl } from '@marketplace-web/ref-url/ref-url-feature'

import { useCreateConversation } from '@marketplace-web/messaging/conversation-messaging-api-resolvers-feature'

import { SIGNUP_URL } from '../../constants/routes'
import { ItemPageAskSellerActionPluginModel } from './types'

type Props = {
  data: ItemPageAskSellerActionPluginModel
}

const ItemPageAskSellerActionPlugin = ({ data }: Props) => {
  const translate = useTranslate('item')
  const { track } = useTracking()
  const refUrl = useRefUrl()
  const { searchParams } = useBrowserNavigation()
  const isClientSide = useIsClient()
  const { createConversation, conversation } = useCreateConversation()
  const conversationNavigator = useConversationNavigator()

  const navigationStartMark = useRef<
    ReturnType<(typeof conversationNavigator)['createNavigationStartMark']> | undefined
  >(undefined)
  const setConversationNavigationStartMark = useCallback(() => {
    navigationStartMark.current = conversationNavigator.createNavigationStartMark()
  }, [conversationNavigator])

  const { user } = useSession()
  const currentUserId = user?.id
  const isUserAuthenticated = !!user

  const [uiState, setUiState] = useState(UiState.Idle)

  const referrerScreen = searchParams.referrer?.toString()
  const searchSessionData = getSearchSessionData()
  const searchSessionIdRef = useRef(
    referrerScreen === 'catalog' ? searchSessionData.searchSessionId : null,
  )

  const { shippingDetails } = useContext(ShippingDetailsContext)

  useEffect(() => {
    if (!conversation?.id) return
    if (uiState !== UiState.Pending) return

    track(
      buyerWantItemEvent({
        itemId: data.itemId,
        transactionId: Number(conversation.id),
        button: 'message',
        globalSearchSessionId: searchSessionData.globalSearchSessionId,
        globalCatalogBrowseSessionId: searchSessionData.globalCatalogBrowseSessionId,
        searchSessionId: searchSessionIdRef.current,
        homepageSessionId: getHomepageSessionIdFromUrl(),
      }),
    )

    conversationNavigator.navigate(conversation.id, {
      navigationStartMark: navigationStartMark.current,
    })
  }, [
    conversation?.id,
    data.itemId,
    searchSessionData.globalSearchSessionId,
    searchSessionData.globalCatalogBrowseSessionId,
    track,
    uiState,
    conversationNavigator,
  ])

  if (!data) return null

  const {
    canBuy,
    canInstantBuy,
    isClosed,
    isHidden,
    isReserved,
    itemId,
    itemUserId,
    reservedForUserId,
  } = data

  const isItemReservedForBuyer = reservedForUserId === currentUserId
  const isViewingOwnItem = itemUserId === currentUserId

  const showPlugin = !isClosed && !isViewingOwnItem

  if (!showPlugin) return null

  const handleClick = () => {
    setConversationNavigationStartMark()
    setUiState(UiState.Pending)

    track(
      clickEvent({
        target: 'write_message',
        targetDetails: JSON.stringify({
          item_id: itemId,
        }),
      }),
    )

    if (!conversation?.id) {
      createConversation({
        itemId,
        receiverId: itemUserId.toString(),
        initiator: ConversationInitiatorType.AskSeller,
      })
    }
  }

  const getUrl = () => {
    if (isUserAuthenticated) return undefined

    const urlQuery: Parameters<typeof urlWithParams>[1] = {
      button_name: 'message',
      ch: 'item',
      receiver_id: itemUserId,
      ref_url: refUrl,
      source: 'wantit',
    }

    if (isClientSide) {
      urlQuery.global_search_session_id = searchSessionData.globalSearchSessionId
    }

    return urlWithParams(SIGNUP_URL, urlQuery)
  }

  const canTransact =
    canBuy &&
    canInstantBuy &&
    (!isReserved || isItemReservedForBuyer) &&
    !isHidden &&
    !!shippingDetails

  return (
    <Button
      text={translate('actions.ask_seller')}
      onClick={handleClick}
      styling={canTransact ? 'outlined' : 'filled'}
      testId="ask-seller-button"
      url={getUrl()}
    />
  )
}

export default ItemPageAskSellerActionPlugin
